import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, User, Heart, MessageCircle, Share2, Bookmark, ThumbsUp, Reply, MoreHorizontal, Globe, Mail, Twitter, Facebook, Linkedin } from 'lucide-react';
import { Post, Comment } from '../../types';
import { getPostById, addComment, getComments, likePost, unlikePost } from '../../services/firebaseService';
import ReportContent from '../Moderation/ReportContent';
import RSSFeedDiscovery from '../RSS/RSSFeedDiscovery';

interface ArticlePageProps {
  post: Post | null;
  onLoadPost: (postId: string) => Promise<void>;
}

const ArticlePage: React.FC<ArticlePageProps> = ({ post, onLoadPost }) => {
  const { postId } = useParams<{ postId: string }>();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (postId && onLoadPost) {
      onLoadPost(postId);
    }
  }, [postId, onLoadPost]);

  useEffect(() => {
    if (post) {
      loadComments();
      loadLikes();
    }
  }, [post]);

  const loadComments = async () => {
    if (!post) return;
    try {
      const commentsData = await getComments(post.id);
      setComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const loadLikes = async () => {
    if (!post) return;
    try {
      // In a real app, you'd have a likes collection
      setLikesCount(post.likes || 0);
      setIsLiked(post.userLiked || false);
    } catch (error) {
      console.error('Error loading likes:', error);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !post) return;

    try {
      const comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'> = {
        postId: post.id,
        authorName: 'Anonymous', // In a real app, get from auth
        authorEmail: 'anonymous@example.com',
        content: newComment.trim(),
        isApproved: true, // Auto-approve for now
        parentId: null,
        likes: 0,
      };

      const commentId = await addComment(comment);
      
      // Create notification for the article author
      if (post.authorId) {
        const { commentNotificationService } = await import('../../services/commentNotificationService');
        await commentNotificationService.createCommentNotification(
          post.authorId,
          post.id,
          commentId,
          comment.authorName,
          comment.authorEmail,
          comment.content,
          'new_comment'
        );
      }
      
      setNewComment('');
      loadComments();
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleLike = async () => {
    if (!post) return;

    try {
      if (isLiked) {
        await unlikePost(post.id);
        setLikesCount(prev => prev - 1);
      } else {
        await likePost(post.id);
        setLikesCount(prev => prev + 1);
      }
      setIsLiked(!isLiked);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = post?.title || '';
    const text = post?.content?.replace(/<[^>]*>/g, '').substring(0, 200) || '';

    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + '\n\n' + url)}`;
        break;
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
    setShowShareModal(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowShareModal(false);
  };

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Globe className="w-3 h-3 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">PostSub.io</span>
            </div>
            <nav className="hidden md:flex space-x-6 lg:space-x-8">
              <a href="/" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm lg:text-base">Home</a>
              <a href="/articles" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm lg:text-base">Articles</a>
              <a href="/newsletters" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm lg:text-base">Newsletters</a>
            </nav>
            <button className="md:hidden p-2 text-gray-600 hover:text-gray-900">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Article Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-8 mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>By {post.authorName || 'Anonymous'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{new Date(post.publishDate || post.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Engagement Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-t border-b border-gray-200 py-4 gap-4">
            <div className="flex items-center gap-2 sm:gap-6">
              <button
                onClick={handleLike}
                className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${
                  isLiked 
                    ? 'bg-red-100 text-red-600' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isLiked ? 'fill-current' : ''}`} />
                <span>{likesCount}</span>
              </button>
              <button className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors text-sm">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>{comments.length}</span>
              </button>
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors text-sm"
              >
                <Share2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
                <Bookmark className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <ReportContent postId={post?.id || ''} />
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-8 mb-6 sm:mb-8">
          <div 
            className="prose max-w-none prose-sm sm:prose-base"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>

        {/* RSS Feed Discovery */}
        {post && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
            <RSSFeedDiscovery 
              feedUrl={`${window.location.origin}/rss/${post.authorId}/newsletter.xml`}
              feedName={post.authorName || 'this newsletter'}
            />
          </div>
        )}

        {/* Comments Section */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
            Comments ({comments.length})
          </h3>

          {/* Add Comment Form */}
          <form onSubmit={handleAddComment} className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm sm:text-base"
                  rows={3}
                />
              </div>
              <button
                type="submit"
                disabled={!newComment.trim()}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base font-medium whitespace-nowrap"
              >
                Post Comment
              </button>
            </div>
          </form>

          {/* Comments List */}
          <div className="space-y-4 sm:space-y-6">
            {comments.map((comment) => (
              <div key={comment.id} className="border-b border-gray-200 pb-4 sm:pb-6 last:border-b-0">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
                      <span className="font-semibold text-gray-900 text-sm sm:text-base">{comment.authorName}</span>
                      <span className="text-xs sm:text-sm text-gray-500">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-3 text-sm sm:text-base leading-relaxed">{comment.content}</p>
                    <div className="flex items-center gap-3 sm:gap-4">
                      <button className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors text-sm">
                        <ThumbsUp className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{comment.likes}</span>
                      </button>
                      <button className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors text-sm">
                        <Reply className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Reply</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {comments.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No comments yet. Be the first to share your thoughts!</p>
            </div>
          )}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Share this article</h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <MoreHorizontal className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => handleShare('twitter')}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Twitter className="w-6 h-6 text-blue-400" />
                <span>Share on Twitter</span>
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Facebook className="w-6 h-6 text-blue-600" />
                <span>Share on Facebook</span>
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Linkedin className="w-6 h-6 text-blue-700" />
                <span>Share on LinkedIn</span>
              </button>
              <button
                onClick={() => handleShare('email')}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Mail className="w-6 h-6 text-gray-600" />
                <span>Share via Email</span>
              </button>
              <button
                onClick={copyToClipboard}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Share2 className="w-6 h-6 text-gray-600" />
                <span>Copy Link</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlePage;
